import { AmbientLight, Scene, Vector3 } from 'three';
import { CustomMouseEvent } from '../controls';
import { Viewer } from '../main';
import LocalScene from '../projections';
import {
  AerialAreaMeasurement,
  AerialLengthMeasurement,
  Measurement3D,
  MeasurementArea,
  MeasurementPoint,
  MinimapMeasure,
  MeasurementConstructor,
  MeasurementFormatter,
  MeasurementGroup,
  MeasurementGroupExporter,
  MeasurementSceneView,
  MeasureEndpoint
} from '.';
import { clearAerialTooltip, clearViewerTooltip } from '../../viewer';
import { RayCaster } from '../ray-caster';

export class MeasurementController {
  public viewer: Viewer;
  public enabled = false;
  public measureType = "standard";

  public formatter: MeasurementFormatter;
  public measureSceneView: MeasurementSceneView;
  public measurementAerialView: MinimapMeasure;
  public measurementBuilder: MeasurementConstructor;
  public measurementGroupExporter: MeasurementGroupExporter;
  public measurementGroup: MeasurementGroup;
  public setMeasurements;
  public scene: Scene;

  constructor(viewer: Viewer, props) {
    const {setMeasurements} = props;

    this.viewer = viewer;
    this.setMeasurements = setMeasurements;

    this.initScene();

    this.formatter = new MeasurementFormatter("m");
    this.measureSceneView = new MeasurementSceneView(this, this.scene);
    this.measurementAerialView = new MinimapMeasure(this);
    this.measurementBuilder = new MeasurementConstructor(this, this.pointclouds);
    this.measurementGroupExporter = new MeasurementGroupExporter(this);
    this.measurementGroup = new MeasurementGroup(this);
  }

  get pointclouds() {
    return this.viewer.pointclouds;
  }

  get controls() {
    return this.viewer.controls;
  }

  get isImageOnly() {
    return this.viewer.isImageOnly;
  }

  get measuring() {
    return this.measurementBuilder.activeMeasurement;
  }

  get drawing() {
    return this.measurementBuilder.activeMeasurement;
  }

  get notEmpty() {
    return this.measurementGroup.measurements.length > 0;
  }

  get saved() {
    return Boolean(this.measurementGroup.databaseID);
  }

  get dragObject() {
    return this.measureSceneView.dragObject;
  }

  get editable() {
    // Always true since view-only mode doesnt exist
    return true;
  }

  get measureExportType() {
    return this.measurementGroupExporter.dataExportFilter;
  }

  get hoveredPoint() {
    return this.measureSceneView.hoveredPoint;
  }

  setState(state) {
    if (state) {
      this.enable();
    } else {
      this.disable(true);
    }
  }

  enable() {
    this.enabled = true;
    this.createNewMeasurementGroup();
    this.measurementAerialView.enabled = true;
    this.measurementAerialView.init();
  }

  disable(clear=false) {
    if (clear) {
      this.measureSceneView.removeAll();
      this.measurementAerialView.clear();
    }

    this.enabled = false;
    this.stopDrawingItems();
    this.measurementGroup.reset();
    this.measurementAerialView.enabled = false;
    this.measurementAerialView.removeInteraction();
    this.viewer.multiImageWindow.close();

    clearAerialTooltip();
    clearViewerTooltip();
  }

  initScene() {
    const scene = new Scene();
    const light = new AmbientLight(0xFFFFFF);
    scene.add(light);
    this.scene = scene;
  }

  loadMeasurementData(data) {
    try {
      const projection = data.projection;
      const sameProjection = LocalScene.currentProjection(projection.name);

      if (!sameProjection) {
        return {
          success: false,
          message: `Projection mismatch. Projection used was '${projection.name}'`
        };
      };

      // Clear data and create new group
      this.createNewMeasurementGroup();

      // Load measurements into new group
      let measurements = data.measurements;
      this.measurementGroup.loadMeasurements(measurements);
      this.refreshMeasurementList();

      return {
        success: true,
        message: `Loaded ${data.count} measurements`
      };
    } catch {
      this.createNewMeasurementGroup();

      return {
        success: false,
        message: "Error loading measurement group"
      };
    }
  }

  cancelMeasurement() {
    this.stopDrawingItems();
    this.viewer.multiImageWindow.close();
  }

  returnCoordinate(obj) {
    return false;
    /*
    return isLineworkObject(obj)
      || isFeatureServerObject(obj)
      || obj instanceof TexturedSprite;
    */
  }

  getCoordinate(event) : Vector3 {
    return this.controls.getPickerResults(event).coordinate;
  }

  /** @private */
  clickMultiImage(event) {
    if (!this.editable) {
      return;
    }

    const measurement = this.measurementBuilder.measurement;
    const multiImage = this.viewer.multiImageWindow;
    const isSinglePoint = measurement instanceof MeasurementPoint;

    // Calculate clicked position
    const raycaster = new RayCaster(this.viewer, event);
    const direction = raycaster.vectorFromEvent(event);
    const position = this.viewer.camera.position;

    multiImage.show({
      closePrompt: () => {
        if (measurement.hasPoints) {
          return {
            title: 'Discard unsaved measurement?',
            text: 'Active measurements found, do you want to discard measurements?',
            callback: () => {
              this.cancelMeasurement();
              clearViewerTooltip();
            }
          };
        }
      },
      onComplete: () => {
        multiImage.getClickedPoint(coordinate => {
          const isFinished = this.onClickFinish(coordinate);

          // Certain measurement types require a simulated double-click
          if (!isFinished) {
            this.onClickFinish(coordinate);
          }

          clearViewerTooltip();
          multiImage.close();
        });
      },
      onAddMore: () => {
        multiImage.getClickedPoint(coordinate => {
          this.onClickFinish(coordinate);
          multiImage.canComplete = measurement.canFinish;
          multiImage.resetObservations();
        });
      },
      canComplete: measurement.canFinish,
      canAddMore: !isSinglePoint,
    });

    multiImage.addFirstObservation(position, direction);
  }

  /** @private */
  clickStandard(event) {
    let coordinate = this.hoveredPoint
      ? this.hoveredPoint.position
      : this.getCoordinate(event);

    if (!coordinate) {
      return;
    }

    this.onClickFinish(coordinate);
  }

  /** @private */
  onClickFinish(coordinate) {
    let isFinished = false;
    let result = this.measurementBuilder.add(coordinate);

    if (!result.valid) {
      this.cancelMeasurement();
      return isFinished;
    }

    if (result.added) {
      this.measureSceneView.addTemporaryPoint(coordinate);
      this.updateMeasureText();
    }

    if (this.measurementBuilder.isFinished) {
      isFinished = true;
      this.finishMeasurement();
    };

    return isFinished;
  }

  onMouseUp(event: CustomMouseEvent): boolean {
    if (event.mouseMoved || !this.enabled) {
      return false;
    }

    if (event.isLeftClick) {
      this.mouseClick(event);
      return true;
    }

    if (event.isRightClick) {
      if (this.measuring) {
        // Cancel measurement
        this.cancelMeasurement();
        return true;
      }
    }

    return false;
  }

  mouseClick(event) {
    if (!this.editable) {
      return;
    }

    if (this.isImageOnly) {
      // Image only multi-image measurements
      this.clickMultiImage(event);
    } else {
      this.clickStandard(event);
    }
  }

  onMouseMoveAerial(event) {
    return this.measurementAerialView.onMouseMove(event);
  }

  onMouseMoveScene(event: CustomMouseEvent) {
    return this.measureSceneView.onMouseMove(event);
  }

  resetDragObject() {
    this.measureSceneView.setDragObject(null);
  }

  finishMeasurement() {
    this.updateMeasureText();
    let measurement = this.measurementBuilder.getMeasurement();

    if (measurement instanceof MeasurementArea) {
      measurement.generateSurface();
    }

    this.measureSceneView.removeTemporaryObjects();
    this.measurementGroup.add(measurement);
    this.renderMeasurement(measurement);
    this.markLastPointDraggable(measurement);
  }

  markLastPointDraggable(measurement) {
    let sceneEndpoints = this.scene.children
      .filter(x => x instanceof MeasureEndpoint) as MeasureEndpoint[];

    let measureEndpoints = sceneEndpoints
      .filter(x => x.measurementID === measurement.id);

    let lastPoint = measureEndpoints[measureEndpoints.length - 1];
    this.measureSceneView.setDragObject(lastPoint);
  }

  stopDrawingItems() {
    this.measurementBuilder.reset();
    this.measureSceneView.removeTemporaryObjects();

    if (this.measurementAerialView.sketch !== null) {
      this.measurementAerialView.clear();
      this.redrawAerialGeometry();
    }

    this.updateMeasureText(false);
  }

  setPointGrabTooltip() {
    this.measureSceneView.setPointGrabTooltip();
  }

  measureGrabFinished() {
    this.measureSceneView.measureGrabFinished();
  }

  refreshMeasurementList() {
    this.setMeasurements([...this.measurementGroup.measurements]);
  }

  updateMeasureText(checkMorePoints = true) {
    if (!this.enabled) {
      return;
    }

    let morepoints = false;
    let measurement = this.measurementBuilder.measurement;

    if (checkMorePoints) {
      morepoints = !this.measurementBuilder.isFinished;
      if (measurement.vertices.length === 0) {
        morepoints = false;
      }
    }

    this.measureSceneView.setTooltipText(measurement, morepoints);
  }

  addAerialMeasurement(geometry) {
    let measurement;

    if (this.measurementAerialView.measurementType === 'LineString') {
      measurement = new AerialLengthMeasurement(geometry);
    } else {
      measurement = new AerialAreaMeasurement(geometry);
    }

    measurement.setController(this);
    this.measurementGroup.add(measurement);
  }

  createNewMeasurementGroup() {
    this.measurementAerialView.clear();
    this.measureSceneView.removeAll();
    this.measurementGroup = new MeasurementGroup(this);
    this.setMeasurementType(this.measureType);
  }

  setMeasurementType(measurementType) {
    if (!this.enabled) {
      return;
    }

    this.measureType = measurementType;
    this.measurementBuilder.setBuilder(measurementType);
    this.measureSceneView.removeTemporaryObjects();
    this.viewer.multiImageWindow.close();

    let measurement = this.measurementBuilder.measurement;
    this.measureSceneView.setTooltipText(measurement);

    this.stopDrawingItems();
    this.measurementAerialView.setMeasurementType(measurementType);
  }

  removeByID(id) {
    this.measureSceneView.removeByID(id);
    this.measurementGroup.removeByID(id);

    this.measurementAerialView.clear();
    this.redrawAerialGeometry();

    clearViewerTooltip();
  }

  setTitle(id, title) {
    const measurements = this.measurementGroup.measurements;
    const measurement = measurements.find(measurement => measurement.id === id);
    if (measurement) {
      measurement.title = title;
      this.refreshMeasurementList();
    }

    clearViewerTooltip();
  }

  redrawAerialGeometry(measurement = null, hover = false) {
    if (measurement) {
      this.measurementGroup.getMeasurements2D().forEach(measurement_ => {
        if (measurement_.id === measurement.id) {
          this.measurementAerialView.loadGeometry(measurement_, hover);
        } else {
          this.measurementAerialView.loadGeometry(measurement_);
        }
      });
    } else {
      this.measurementGroup.getMeasurements2D().forEach(measurement => {
        this.measurementAerialView.loadGeometry(measurement);
      });
    }
  }

  clearByID(id) {
    this.measureSceneView.removeByID(id);
  }

  removeAll() {
    this.measurementGroup.removeAll();
    this.clearViews();
  }

  clearViews() {
    this.measureSceneView.removeAll();
    this.measurementAerialView.clear();
  }

  export(type, ordering) {
    if (type === "csv") {
      this.exportCSV(ordering);
    } else if (type === "dat") {
      this.exportDAT();
    }
  }

  exportDAT() {
    this.measurementGroupExporter
      .exportDAT(this.measurementGroup);
  }

  exportCSV(ordering) {
    this.measurementGroupExporter.exportCSV(
      this.measurementGroup, ordering);
  }

  /* Not implemented */
  exportLinework(type, projection, version) {
    this.measurementGroupExporter
      .exportLinework(this.measurementGroup, type, version);
  }

  setUnits(units) {
    this.formatter.setUnits(units);

    if (this.measurementGroup) {
      this.reload();
    }
  }

  reload() {
    this.clearViews();
    this.measurementGroup.measurements.forEach(measurement => {
      this.renderMeasurement(measurement);
    });
    this.refreshMeasurementList();
  }

  updateRenderColor(measurement, hover = false) {
    if (measurement instanceof Measurement3D) {
      this.measureSceneView.updateMaterial(measurement.id, hover);
    } else {
      this.measurementAerialView.clear();
      this.redrawAerialGeometry(measurement, hover);
    }
  }

  renderMeasurement(measurement) {
    if (measurement instanceof Measurement3D) {
      this.measureSceneView.draw(measurement);
    } else {
      this.measurementAerialView.loadGeometry(measurement);
    }
  }

  update(changeDetector) {
    this.measureSceneView.update(changeDetector);
  }
}
